import { Ability, AbilityBuilder, AbilityClass } from '@casl/ability'

import type { Actions, Subjects, PermissionMap } from '../types/permissions.types'

export type AppAbility = Ability<[Actions, Subjects]>
export const AppAbility = Ability as AbilityClass<AppAbility>

export const buildAbility = (permissions: string[], isAdmin: boolean = false): AppAbility => {
    const { can, build } = new AbilityBuilder<AppAbility>(AppAbility)

    // Admin can do everything
    if (isAdmin) {
        can('manage', 'all')
        return build()
    }

    // Parse permissions from API response
    const permissionMap = parsePermissions(permissions)

    // Define abilities based on permissions
    Object.entries(permissionMap).forEach(([subject, actions]) => {
        actions.forEach((action) => {
            can(action, subject as Subjects)
        })
    })

    return build()
}

// Validate permission code format (dot notation)
const validatePermissionFormat = (permission: string): boolean => {
    return /^[a-z][a-z_]*(\.[a-z][a-z_]*)*$/.test(permission)
}

export const parsePermissions = (permissions: string[]): PermissionMap => {
    const map: PermissionMap = {}

    permissions.forEach((permission) => {
        // Validate permission format first
        if (!validatePermissionFormat(permission)) {
            return
        }

        // Split by dot for dot notation permissions
        const parts = permission.split('.')
        if (parts.length < 2) {
            return
        }

        const subject = parts[0]
        const action = parts.slice(1).join('.') // Handle nested permissions like "employee.view.personal"

        if (!subject || !action) {
            return
        }

        // Normalize subject name for CASL (first letter uppercase)
        const normalizedSubject = subject.charAt(0).toUpperCase() + subject.slice(1)

        if (!map[normalizedSubject]) {
            map[normalizedSubject] = []
        }

        map[normalizedSubject].push(action as Actions)
    })

    // Add legacy permission mappings for backward compatibility
    addLegacyPermissionMappings(map, permissions)

    return map
}

/**
 * Maps backend permission codes to legacy frontend action+subject format
 * SECURITY CRITICAL: This ensures route guards work properly during migration
 */
function addLegacyPermissionMappings(map: PermissionMap, permissions: string[]): void {
    // HRIS Import permission mappings
    if (permissions.includes('hris_import.upload')) {
        if (!map['Hris_import']) map['Hris_import'] = []
        map['Hris_import'].push('upload')
    }

    if (permissions.includes('hris_import.view_jobs')) {
        if (!map['Hris_import']) map['Hris_import'] = []
        map['Hris_import'].push('view_jobs')
    }

    if (permissions.includes('hris_import.view_results')) {
        if (!map['Hris_import']) map['Hris_import'] = []
        map['Hris_import'].push('view_results')
    }

    // Employee permission mappings
    if (permissions.includes('employee.view.self')) {
        if (!map['Employee']) map['Employee'] = []
        map['Employee'].push('view.self')
    }

    if (permissions.includes('employee.edit.self')) {
        if (!map['Employee']) map['Employee'] = []
        map['Employee'].push('edit.self')
    }

    if (permissions.includes('employee.view.team')) {
        if (!map['Employee']) map['Employee'] = []
        map['Employee'].push('view.team')
    }

    if (permissions.includes('employee.view.all')) {
        if (!map['Employee']) map['Employee'] = []
        map['Employee'].push('view.all')
    }

    if (permissions.includes('employee.manage.documents')) {
        if (!map['Employee']) map['Employee'] = []
        map['Employee'].push('manage.documents')
    }

    // Integration permission mappings
    if (permissions.includes('integrations.admin.install') || permissions.includes('integrations.admin.manage')) {
        if (!map['Integrations']) map['Integrations'] = []
        map['Integrations'].push('admin.manage')
    }

    // Analytics permission mappings
    if (permissions.includes('analytics.view.overview')) {
        if (!map['Analytics']) map['Analytics'] = []
        map['Analytics'].push('view.overview')
    }

    if (permissions.includes('analytics.view.team')) {
        if (!map['Analytics']) map['Analytics'] = []
        map['Analytics'].push('view.team')
    }

    // Survey permission mappings
    if (permissions.includes('surveys.view')) {
        if (!map['Surveys']) map['Surveys'] = []
        map['Surveys'].push('view')
    }

    // Kudos permission mappings
    if (permissions.includes('kudos.view')) {
        if (!map['Kudos']) map['Kudos'] = []
        map['Kudos'].push('view')
    }

    // Dashboard and Profile permissions (everyone gets these)
    if (permissions.length > 0) {
        if (!map['Dashboard']) map['Dashboard'] = []
        map['Dashboard'].push('view')

        if (!map['Profile']) map['Profile'] = []
        map['Profile'].push('view')
    }
}

export const buildEmptyPermissions = (): AppAbility => {
    const { build } = new AbilityBuilder<AppAbility>(AppAbility)
    return build()
}

export const updateAbility = (ability: AppAbility, permissions: string[]): void => {
    const newAbility = buildAbility(permissions)
    ability.update(newAbility.rules)
}
